import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, ArrowLeft, ArrowRight } from 'lucide-react';
import { InlineLoginForm } from './inline-login-form';
import { auth, usesLocalMondayRedirect, usesZohoFixedIp } from '@/config/firebase';
import { getEffectiveUid, loadUserDocOnce } from '@/stores/auth-store';
import { getTPADisplayName, getSaaSPrettyName, EXTENSION_NAME_PATTERN } from '@/config/constants';

/**
 * Cloud Functions base URL.
 * In local development, use the Firebase emulator; in production, use the live URL.
 * Mirrors APP_ENV_SERVER_URL from the old firebase-things.js.
 */
const CF_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') ? 'https://fluidal-geophilous-ellyn.ngrok-free.dev/ulgebra-license/us-central1'
  // Same runtime switch as `config/firebase.ts`: the browser navigates to the
  // callback itself, so the local emulator needs no tunnel.
  : (import.meta.env.DEV && typeof window !== 'undefined' && window.location.hostname === 'localhost' && localStorage.getItem('useFunctionEmulatorDev') === 'true') ? 'http://localhost:5001/ulgebra-license/us-central1'
  : 'https://us-central1-ulgebra-license.cloudfunctions.net';

/**
 * HubSpot client ID map — used when the callback doesn't include client_id.
 * Ported from oauth-home.html.
 */
const HUBSPOT_CLIENT_MAP: Record<string, string> = {
  nonadminauthforhubspotcrm: '4110ff15-cae1-4a4d-af05-5bcbf90928d7',
  acuityschedulingforhubspotcrm: '106961a0-ec87-4d08-8188-f08f154adbb0',
  lookupforhubspotcrm: '870ca14e-3b45-4332-9636-32168ff05b12',
  calendlyforhubspotcrm: 'e6c60ffe-b50a-4842-8a53-e67d95c94136',
  twilioforhubspotcrm: '28e3fcba-92f6-4301-8a82-7381f8b658a9',
  messagebirdforhubspotcrm: '9be1eb84-88d7-4bbe-972b-f507b913f504',
  whatsappforhubspotcrm: '5ce62ce4-e697-423c-b878-a2b28f9dd297',
  textlocalforhubspotcrm: '217e8bc7-73b3-4b0b-9a2f-e5e8eb756f3d',
  pinnacleforhubspotcrm: 'f30b42b5-9aca-43df-8da5-fc91ac136ed0',
  karixforhubspotcrm: '18454e29-584d-422e-be15-a248b7d0af9b',
  grapevineforhubspotcrm: '5a189156-eb29-41e5-b3a9-43dd1cbe7cdc',
  routemobileforhubspotcrm: '19a87901-c92c-4901-9b96-d7775db63b59',
  rivetforhubspotcrm: 'f5b28c25-572a-4c91-a5fc-2f300dc4bdeb',
  vonageforhubspotcrm: 'df4b5c87-6315-41eb-93a0-a59314df0a38',
  ringcentralforhubspotcrm: '91ef3af9-c7b9-40a7-bbe9-650fd7ab5323',
  clickatellforhubspotcrm: 'a1fc08b9-ceb5-4fff-98ef-6c1af7220e68',
  stripeforhubspotcrm: '02b945fe-9cdb-445c-aec7-1f7b4cb5b200',
  googledriveforhubspotcrm: '0cc9bdc5-b5e0-4b45-8383-2992786bde96',
};

/**
 * Shopify client ID map — ported from oauth-home.html.
 */
const SHOPIFY_CLIENT_MAP: Record<string, string> = {
  pinnacleforshopify: '8e760b4d84de29b76018581e4c0f2598',
  twilioforshopify: 'd17e3d73885c6dd7d3d61a4ab7d8ad84',
};

/** Logo image with graceful fallback — hides if image fails to load */
function LogoImg({ src, alt, size = 32 }: { src: string; alt: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className="rounded-lg object-contain"
      onError={() => setFailed(true)}
    />
  );
}

export function OAuthCallback() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'loading' | 'needs-login' | 'processing' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  /** True when we signed the user out because their UID didn't match the
   *  state's UID — used to render a clarifying banner above the login form
   *  so the user understands why they're being asked to sign in again. */
  const [wrongUser, setWrongUser] = useState(false);

  /** Parse state's UID from the URL once — used both by the initial
   *  identity check and the post-login verification. Returns null if state
   *  is missing or doesn't carry a UID prefix (legacy auth shapes). */
  const stateUserIdFromUrl = useMemo<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = new URLSearchParams(window.location.search).get('state');
    if (!raw || !raw.includes(':::')) return null;
    return raw.split(':::')[0] || null;
  }, []);

  // Parse appCode from URL to derive TPA + SaaS display names and logo paths
  const { tpaName, saasName, tpaLogo, saasLogo, appCode: parsedAppCode } = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    const stateParam = query.get('state');
    const ac = stateParam?.includes(':::')
      ? stateParam.split(':::')[1]
      : query.get('appCode') || '';

    const match = ac.match(EXTENSION_NAME_PATTERN); // e.g. "twilioforzohocrm" → ["twilio", "zohocrm"]
    const tpa = match ? match[1] : ac;
    const saas = match ? match[2] : '';

    return {
      tpaName: getTPADisplayName(tpa),
      saasName: saas ? getSaaSPrettyName(saas) : '',
      tpaLogo: `/images/logos/${tpa}.png`,
      saasLogo: saas ? `/images/logos/${saas}.png` : '',
      appCode: ac,
    };
  }, []);

  // On mount: wait for Firebase auth to resolve, then decide.
  // If a user is signed in but their UID doesn't match the state's UID
  // (e.g. cross-origin redirect lands on a domain with a different
  // session, or the user switched accounts mid-flow), sign them out
  // and route to the login form. Matches legacy oauth-home.html
  // behavior at line 195-199 (signOut + reload), but in-page so the
  // OAuth code/state in the URL survive the re-login.
  useEffect(() => {
    auth.authStateReady().then(async () => {
      const current = auth.currentUser;
      if (current && stateUserIdFromUrl && current.uid !== stateUserIdFromUrl) {
        console.warn('[OAuthCallback] Signed-in UID', current.uid, 'differs from state UID', stateUserIdFromUrl, '— signing out and prompting re-login');
        try {
          await auth.signOut();
        } catch (err) {
          console.warn('[OAuthCallback] signOut failed (continuing):', err);
        }
        setWrongUser(true);
        setUserEmail(null);
        setPhase('needs-login');
        return;
      }
      if (current) {
        setUserEmail(current.email);
        setPhase('processing');
      } else {
        setPhase('needs-login');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-continue with countdown when user is logged in
  useEffect(() => {
    if (phase !== 'processing' || !auth.currentUser) return;
    if (countdown <= 0) {
      handleOAuthCallback(auth.currentUser.uid).catch((err) => {
        console.error('[OAuthCallback] Error:', err);
        setError(saasName
          ? `Could not connect ${tpaName} with ${saasName}. Please try again.`
          : (err instanceof Error ? err.message : 'Authorization failed. Please try again.')
        );
        setPhase('error');
      });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, countdown]);

  // Called by InlineLoginForm after successful sign-in.
  // Re-verify identity against state's UID — otherwise the user could
  // sign in as the wrong account on retry and we'd silently accept it.
  async function onLoginSuccess() {
    const current = auth.currentUser;
    if (!current) return;

    if (stateUserIdFromUrl && current.uid !== stateUserIdFromUrl) {
      console.warn('[OAuthCallback] Login succeeded but UID', current.uid, 'still differs from state UID', stateUserIdFromUrl, '— signing out');
      try {
        await auth.signOut();
      } catch { /* non-fatal */ }
      setWrongUser(true);
      setUserEmail(null);
      // Stay on needs-login phase so the user sees the banner + form again.
      return;
    }

    setWrongUser(false);
    setUserEmail(current.email);
    setCountdown(5);
    setPhase('processing');
  }

  // User wants to use a different account
  async function onSignOut() {
    await auth.signOut();
    setUserEmail(null);
    setCountdown(5);
    setPhase('needs-login');
  }

  // Retry after error
  function onRetry() {
    setError(null);
    setCountdown(5);
    setPhase('processing');
  }

  // Connection logos — TPA ↔ SaaS
  const connectionLogos = (size = 32) => (
    <div className="flex items-center justify-center gap-3">
      <LogoImg src={tpaLogo} alt={tpaName} size={size} />
      {saasLogo && (
        <>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <LogoImg src={saasLogo} alt={saasName} size={size} />
        </>
      )}
    </div>
  );

  async function handleOAuthCallback(userUid: string) {
    // ── Local dev bounce-back ──────────────────────────────────────────
    const query0 = new URLSearchParams(window.location.search);
    const state0 = query0.get('state') || '';
    const localPortMatch = state0.match(/:::localPort=(\d+)$/);
    if (localPortMatch && window.location.hostname !== 'localhost') {
      const localPort = localPortMatch[1];
      const cleanState = state0.replace(/:::localPort=\d+$/, '');
      const bounceParams = new URLSearchParams(window.location.search);
      bounceParams.set('state', cleanState);
      window.location.href = `http://localhost:${localPort}/oauth-home?${bounceParams.toString()}`;
      return;
    }

    const query = new URLSearchParams(window.location.search);
    const pathSegments = window.location.pathname.split('/').filter(Boolean);

    let stateParam = query.get('state') || null;
    const code = query.get('code') || null;

    if (!stateParam && pathSegments.length > 1) {
      stateParam = decodeURIComponent(pathSegments.slice(1).join('/'));
    }

    const isRedirect = stateParam === 'isRedirect';
    if (isRedirect) stateParam = null;

    let appCode = stateParam?.includes(':::')
      ? stateParam.split(':::')[1]
      : query.get('appCode');

    const stateUserId = stateParam?.includes(':::')
      ? stateParam.split(':::')[0]
      : null;

    if (!appCode) {
      if (pathSegments.length > 1) {
        const pathState = decodeURIComponent(pathSegments.slice(1).join('/'));
        if (pathState.includes(':::')) {
          appCode = pathState.split(':::')[1];
        }
      }
    }

    if (!appCode) {
      setError('Invalid authorization link. Please start the installation again from your CRM marketplace.');
      return;
    }

    // Use the currently signed-in user's UID. The stateUserId may differ
    // (e.g. CRM platform user who installed the app vs the Ulgebra account
    // signing in now). Always prefer the authenticated user.
    const uid = getEffectiveUid() || userUid;

    if (stateUserId && stateUserId !== uid) {
      console.warn('[OAuthCallback] State UID', stateUserId, 'differs from signed-in UID', uid, '— using signed-in user');
    }

    // Google OAuth (Google Drive, Google Sheets)
    if (appCode.startsWith('google') && stateParam?.split(':::').length! > 2 && stateParam?.split(':::')[2] === 'isGoogleAuth') {
      const tpaCode = appCode.split('for')[0];
      const appType = tpaCode === 'googledrive' ? 'tpa' : '';
      window.location.href = `${CF_BASE}/new_oauthCallbackGoogle?appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}&appType=${appType}`;
      return;
    }

    // Calendly OAuth — only when state param explicitly has the oauthProvider=calendly marker.
    // Without this marker, appCodes like "calendlyforpipedrive" correctly route to the
    // Pipedrive handler below (matching legacy oauth-home.html behavior).
    if (stateParam?.includes('oauthProvider=calendly')) {
      window.location.href = `${CF_BASE}/oauthCallbackCalendly?appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // Bitrix24 — redirect to new_bitrix24AppHandler CF with action=settings
    // Bitrix24 OAuth sends domain and server_domain as query params alongside code/state.
    // Matches original Cloud Function: new_bitrix24AppHandler → new_oauthCallbackBitrix24
    if (appCode.includes('bitrix24')) {
      const domain = query.get('domain') || '';
      const serverDomain = query.get('server_domain') || '';
      window.location.href = `${CF_BASE}/new_bitrix24AppHandler?action=settings&code=${code}&state=${stateParam}&domain=${domain}&server_domain=${serverDomain}&appCode=${appCode}`;
      return;
    }

    // Zoho CRM
    if (appCode.includes('zoho')) {
      // The `_fixedip` copy exchanges the code from 35.202.240.94 (see usesZohoFixedIp).
      // The user doc flag may not be loaded on this page yet, so read it here (Zoho logins are rare).
      let fixedIp = usesZohoFixedIp();
      if (!fixedIp) {
        try {
          fixedIp = !!(await loadUserDocOnce(uid))?.useZohoFixedIp;
        } catch { /* keep the normal callback */ }
      }
      const zohoCallback = fixedIp ? 'new_oauthCallbackZoho_fixedip' : 'new_oauthCallbackZoho';
      window.location.href = `${CF_BASE}/${zohoCallback}?client_id=${query.get('client_id') || ''}&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}&accounts-server=${query.get('accounts-server') || ''}`;
      return;
    }

    // HubSpot CRM
    if (appCode.includes('hubspot')) {
      if (!stateParam?.includes(':::')) {
        stateParam = `${uid}:::${appCode}`;
      }
      // HubSpot does NOT echo client_id back on the callback (per OAuth spec).
      // For non-admin auth, the URL has ?appCode=nonadminauthforhubspotcrm
      // (preserved from our redirect_uri), but `state` carries the host
      // extension (e.g. twilioforhubspotcrm) — and the appCode-from-state
      // override above resolves to the host extension. So the URL's appCode
      // is the only signal that tells us "this is the non-admin flow".
      // Prefer it for the client_id lookup; fall back to the state-resolved
      // appCode if URL's is missing or unmapped.
      // Example callback URL:
      //   /oauth-home?appCode=nonadminauthforhubspotcrm
      //              &code=…&state=${uid}:::twilioforhubspotcrm
      let clientId = query.get('client_id');
      if (!clientId) {
        const urlAppCode = query.get('appCode');
        if (urlAppCode && HUBSPOT_CLIENT_MAP[urlAppCode]) {
          clientId = HUBSPOT_CLIENT_MAP[urlAppCode];
        } else if (HUBSPOT_CLIENT_MAP[appCode]) {
          clientId = HUBSPOT_CLIENT_MAP[appCode];
        }
      }
      if (!clientId) {
        clientId = '4110ff15-cae1-4a4d-af05-5bcbf90928d7';
      }
      window.location.href = `${CF_BASE}/new_oauthCallbackHubspot?client_id=${clientId}&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}&accounts-server=${query.get('accounts-server') || ''}`;
      return;
    }

    // Pipedrive
    if (appCode.includes('pipedrive')) {
      window.location.href = `${CF_BASE}/pipedriveOauthCallback?client_id=${query.get('client_id') || ''}&appCode=${appCode}&uid=${uid}&code=${code}`;
      return;
    }

    // Airtable
    if (appCode.includes('airtable')) {
      window.location.href = `${CF_BASE}/new_oauthCallbackairtable?client_id=${query.get('client_id') || ''}&appCode=${appCode}&uid=${uid}&code=${code}&state=${query.get('state') || ''}`;
      return;
    }

    // Shopify
    if (appCode.includes('shopify')) {
      const shop = query.get('shop');
      if (shop && !code) {
        // Step 1: redirect to Shopify OAuth
        let clientId = appCode.includes(':::') ? appCode.split(':::')[1] : '';
        if (!clientId) clientId = SHOPIFY_CLIENT_MAP[appCode] || '';
        if (!stateParam || isRedirect) stateParam = `${uid}:::${appCode}`;
        const shopifyUrl = `https://${shop}/admin/oauth/authorize?client_id=${clientId}&scope=read_customers&state=${stateParam}&grant_options[]=offline&redirect_uri=https://sms.ulgebra.com/oauth-home?appCode=${appCode}`;
        window.location.href = shopifyUrl;
      } else if (shop && code) {
        // Step 2: send code to backend. A custom app's client_id rides on
        // the raw ?appCode= (…forshopify:::<id>); the state-derived appCode
        // drops it on the Shopify round-trip, so read the raw param here.
        const rawAppCode = query.get('appCode') || appCode;
        const cleanAppCode = rawAppCode.split(':::')[0];
        let clientId = rawAppCode.includes(':::') ? rawAppCode.split(':::')[1] : '';
        if (!clientId) clientId = SHOPIFY_CLIENT_MAP[cleanAppCode] || '';
        if (!stateParam || isRedirect) stateParam = `${uid}:::${cleanAppCode}`;
        window.location.href = `${CF_BASE}/new_oauthCallbackShopify?appCode=${cleanAppCode}&client_id=${clientId}&uid=${uid}&code=${code}&state=${stateParam}&shop=${shop}${isRedirect ? '&isRedirect=true' : ''}`;
      }
      return;
    }

    // Zendesk Sell
    if (appCode.includes('zendesksell')) {
      stateParam = `${uid}:::${appCode}`;
      if (code) {
        window.location.href = `${CF_BASE}/new_oauthCallbackZendeskSell?client_id=${query.get('client_id') || ''}&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      }
      return;
    }

    // Salesforce
    if (appCode.includes('salesforce') || appCode.includes('salesporce')) {
      window.location.href = `${CF_BASE}/new_oauthCallbackSalesForce?client_id=3MVG9fe4g9fhX0E4uq40a93_a6DU9vpAoyQlEjOJYavAfv_hwVSkMYbJVmCtXKcl3KElHh_8phLy21fJJpSbO&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // Dynamics CRM
    if (appCode.includes('dynamicscrm')) {
      const apiUri = stateParam?.split(':::')[0] || '';
      stateParam = `${uid}:::${appCode}`;
      if (code && apiUri) {
        window.location.href = `${CF_BASE}/oauthCallbackDynamicscrm?resource=${apiUri}&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      }
      return;
    }

    // Slack
    if (appCode.includes('forslack')) {
      window.location.href = `${CF_BASE}/new_oauthCallbackSlack?client_id=3MVG9fe4g9fhX0E4uq40a93_a6DU9vpAoyQlEjOJYavAfv_hwVSkMYbJVmCtXKcl3KElHh_8phLy21fJJpSbO&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // WhatsApp for Page (Facebook)
    if (appCode.startsWith('whatsappforpage')) {
      window.location.href = `${CF_BASE}/new_oauthCallbackFacebookLogin?client_id=${query.get('client_id') || ''}&appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // Monday.com
    if (appCode.includes('formonday')) {
      const mondayQuery = new URLSearchParams(window.location.search.replace('?code', '&code'));
      const mondayState = mondayQuery.get('state');
      const subdomain = mondayState?.split(':::')[2] || '';
      stateParam = `${uid}:::${appCode}`;
      const mondayClientId = mondayQuery.get('client_id') || '';
      // Local dev against the emulator: the authorize step named a localhost
      // redirect_uri (see the monday module), and monday's token exchange must
      // repeat the same value. Only the emulator honours it, so the same switch
      // decides here — a localhost client on production functions, or an app
      // that did not register the localhost URL, sends none.
      const localRedirect = usesLocalMondayRedirect(appCode)
        ? `&redirect_uri=${encodeURIComponent(`${window.location.origin}/oauth-home?appCode=${appCode}&client_id=${mondayClientId}`)}`
        : '';
      window.location.href = `${CF_BASE}/new_mondayOauthCallback?client_id=${mondayClientId}&appCode=${appCode}&uid=${uid}&code=${mondayQuery.get('code')}&state=${stateParam}&domain=${subdomain}${localRedirect}`;
      return;
    }

    // Google Spreadsheets
    if (appCode.includes('forgspreadsheets')) {
      window.location.href = `${CF_BASE}/new_oauthCallbackGoogle?appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // RingCentral
    if (appCode.includes('ringcentral')) {
      window.location.href = `${CF_BASE}/oauthCallbackRingcentral?appCode=${appCode}&uid=${uid}&code=${code}&state=${stateParam}`;
      return;
    }

    // Unknown CRM — show error
    setError(saasName
      ? `Could not connect ${tpaName} with ${saasName}. The integration "${appCode}" is not recognized. Please contact support.`
      : `"${appCode}" is not a recognized integration. Please contact support.`
    );
  }

  // User info bar — shown in top-right corner when logged in
  const userInfoBar = userEmail ? (
    <div className="fixed top-3 right-3 z-50 flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-sm text-xs">
      <span className="text-muted-foreground">{userEmail}</span>
      <button
        onClick={onSignOut}
        className="text-destructive hover:text-destructive/80 font-medium transition-colors"
      >
        Sign out
      </button>
    </div>
  ) : null;

  // ── Login phase ──
  if (phase === 'needs-login') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-full max-w-sm p-6 space-y-4">
          {wrongUser && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                You were signed in with a different account than the one that
                started this authorization. Please sign in with the original
                account to complete the connection.
              </p>
            </div>
          )}
          <InlineLoginForm onSuccess={onLoginSuccess} />
        </div>
      </div>
    );
  }

  // ── Error phase ──
  if (error) {
    return (
      <>
        {userInfoBar}
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="max-w-md text-center space-y-5 p-8">
            <img src="/images/ulgebra-icon.png" alt="Ulgebra" className="h-10 w-10 mx-auto opacity-60" />
            {parsedAppCode && connectionLogos(28)}
            <div>
              <h2 className="text-lg font-semibold text-destructive">Authorization Failed</h2>
              <p className="text-sm text-muted-foreground mt-2">{error}</p>
            </div>
            <div className="flex items-center justify-center gap-3">
              {userEmail && (
                <button
                  onClick={onRetry}
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Try Again
                </button>
              )}
              <button
                onClick={() => navigate('/send-sms')}
                className="flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Go Back
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Processing / countdown phase ──
  return (
    <>
      {userInfoBar}
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-5 p-8">
          <div>
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
            <p className="text-sm font-medium mt-3">
              Connecting <strong>{saasName || tpaName}</strong>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {countdown > 0 ? `Redirecting in ${countdown}s...` : 'Redirecting...'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
